import { PostHog } from "posthog-js";

import { PosHogWebEventName } from "./analyticsEvents";

/**
 * Identify a user in PostHog.
 */
export const identifyUser = (
  posthog: PostHog | undefined,
  userId: string,
  userProperties?: Record<string, any>, // Optional user properties to set
): void => {
  if (!posthog || !isPostHogReady(posthog)) {
    return;
  }

  try {
    posthog.identify(userId, userProperties);
    console.log(`✅ Analytics: User identified successfully: ${userId}`);
  } catch (error) {
    console.error(`❌ Analytics: Failed to identify user: ${userId}`, error);
  }
};

/**
 * Reset PostHog identification (for logout).
 */
export const resetUser = (posthog: PostHog | undefined): void => {
  if (!posthog || !isPostHogReady(posthog)) {
    return;
  }

  try {
    posthog.reset();
    console.log("✅ Analytics: User identification reset successfully");
  } catch (error) {
    console.error("❌ Analytics: Failed to reset user identification", error);
  }
};

/**
 * Utility function to safely capture PostHog events with proper timing.
 * Use when staying on the same page (no hard navigation) and just need to fire an event.
 */
export const captureAnalyticsEvent = async (
  posthog: PostHog | undefined,
  eventName: PosHogWebEventName,
  properties?: Record<string, any>, // Optional event properties
  waitForQueue = false, // Wait for event to be queued (useful before page navigation)
): Promise<boolean> => {
  if (!posthog) {
    return false;
  }

  if (typeof posthog.capture !== "function") {
    console.warn("⚠️ Analytics: PostHog capture method not available");
    return false;
  }

  try {
    // Capture the event
    posthog.capture(eventName, properties);
    console.log(`✅ Analytics: Event captured successfully: ${eventName}`);

    // If we need to wait for the event to be queued (before page navigation)
    if (waitForQueue) {
      return new Promise((resolve) => {
        // Increased wait time to ensure event is properly queued
        // PostHog batches events and sends them periodically
        setTimeout(() => {
          console.log(
            `🚀 Analytics: Event queued, safe to proceed: ${eventName}`,
          );
          resolve(true);
        }, 500);
      });
    }

    return true;
  } catch (error) {
    console.error(`❌ Analytics: Failed to capture event: ${eventName}`, error);
    return false;
  }
};

/**
 * Capture an event and then navigate/redirect.
 * Use when need extra safeguards so the event isn't lost.
 */
export const captureEventAndRedirect = async (
  posthog: PostHog | undefined,
  eventName: PosHogWebEventName,
  redirectUrl: string, // URL to redirect to after event is captured
  properties?: Record<string, any>, // Optional event properties
): Promise<void> => {
  console.log(
    `🔍 Analytics: Capture and redirect - Event: ${eventName}, URL: ${redirectUrl}`,
  );

  // Try multiple capture strategies for better reliability
  let eventCaptured = false;

  try {
    // Strategy 1: Use our standard capture with wait
    eventCaptured = await captureAnalyticsEvent(
      posthog,
      eventName,
      properties,
      true, // Wait for queue
    );

    // Strategy 2: If standard capture failed, try direct capture with longer wait
    if (!eventCaptured && posthog && posthog.capture) {
      posthog.capture(eventName, properties);
      // Wait longer for direct capture
      await new Promise((resolve) => setTimeout(resolve, 750));
      eventCaptured = true;
    }
  } catch (error) {
    console.error(`❌ Analytics: All capture strategies failed:`, error);
  }

  // Try to flush queued events before leaving the page
  try {
    if (posthog && typeof (posthog as any).flush === "function") {
      await (posthog as any).flush();
    }
  } catch (e) {
    console.warn("⚠️ Analytics: Flush before redirect failed", e);
  }

  // Add a small additional buffer before redirect to ensure event transmission
  setTimeout(() => {
    console.log(`🔍 Analytics: About to redirect to: ${redirectUrl}`);
    window.location.href = redirectUrl;
  }, 100);
};

/**
 * Check if PostHog is ready to capture events.
 */
export const isPostHogReady = (posthog: PostHog | undefined): boolean => {
  if (!posthog) {
    console.warn("⚠️ Analytics: PostHog instance not available");
    return false;
  }

  // Check if PostHog has the capture method (most essential for our use case)
  const hasCapture = typeof posthog.capture === "function";
  return hasCapture;
};

/**
 * Force flush PostHog events queue (useful before critical redirects)
 */
export const flushPostHogEvents = async (
  posthog: PostHog | undefined,
): Promise<void> => {
  if (!posthog) {
    return;
  }

  try {
    // PostHog has an internal flush method to force send queued events
    if (typeof (posthog as any).flush === "function") {
      await (posthog as any).flush();
      console.log("✅ Analytics: PostHog events flushed successfully");
    } else {
      console.warn("⚠️ Analytics: PostHog flush method not available");
    }
  } catch (error) {
    console.error("❌ Analytics: Failed to flush PostHog events:", error);
  }
};
